import React from "react";
import ApiKeyContext from "../contexts/ApiKeyContext";
import BookmarkItem from "../models/BookmarkItem";
import CatalogContext from "../contexts/CatalogContext";
import { useCustomer } from "../auth/useCustomer";
import { useAuth } from "../auth/useAuth";
import Catalog from "../models/Catalog";

const BookmarkContext = React.createContext({
	/** @type BookmarkItem[] */ bookmarkItems: null,
	addBookmarkItem: async itemId => { },
	setBookmarkItems: () => { },
	getBookmarkItems: () => { },
	clearBookmarkItems: async () => { },
	removeBookmarkItem: async itemId => { },
	removeBookmarkItems: async itemIds => { },
	/** @returns Promise<Boolean>  */ isAllowed: catalog => { },
	sidebarLayoutMode: undefined,
	setSidebarLayoutMode: val => { },
});

export const useBookmarkContext = () => {
	const context = React.useContext(BookmarkContext);
	if (!context) {
		throw new Error(
			"useBookmarkContext must be used within a BookmarkContextProvider"
		);
	}
	return context;
};

export const BookmarkContextProvider = ({ children }) => {
	const apiContext = React.useContext(ApiKeyContext);
	const [catalog] = React.useContext(CatalogContext);
	const CustomerContext = useCustomer();
	const { currentUser } = useAuth();

	const [bookmarkItems, setBookmarkItems] = React.useState(null);
	const [loading, setLoading] = React.useState(false);
	const [sidebarLayoutMode, setSidebarLayoutMode] = React.useState(undefined);

	let lastBookmarksFetch = 0;

	const getBookmarkItems = async () => {
		if (!isAllowed(catalog) || loading || (Date.now() - lastBookmarksFetch) < 5_000) {
			return;
		}
		lastBookmarksFetch = Date.now();

		setLoading(true);
		const bookmarkItems = await BookmarkItem.SearchBy();
		setBookmarkItems(bookmarkItems);

		if (CustomerContext.customer) {
			const associateBookmarkItemIds = bookmarkItems.map(it => it.item?.id);
			const customerBookmarksList = await CustomerContext.getBookmarksList();
			const customerBookmarkItemIds = customerBookmarksList?.items?.map(it => it.item?.id);

			const associateBookmarkItemIdsSet = new Set(associateBookmarkItemIds);
			const customerBookmarkItemIdsSet = new Set(customerBookmarkItemIds);
			const associateItemIdsNotInCustomer = associateBookmarkItemIdsSet.difference(customerBookmarkItemIdsSet);
			const customerItemIdsNotInAssociate = customerBookmarkItemIdsSet.difference(associateBookmarkItemIdsSet);

			const saves = [];
			associateItemIdsNotInCustomer.forEach(it => {
				saves.push(CustomerContext.addBookmark(it));
			});

			const savedAssociateBookmarkItems = [];
			customerItemIdsNotInAssociate.forEach(it => {
				const newBookmarkItem = new BookmarkItem();
				newBookmarkItem.item = it;
				savedAssociateBookmarkItems.push(newBookmarkItem);
				saves.push(newBookmarkItem.save());
			});

			await Promise.allSettled(saves);
			setBookmarkItems(cur => [...(cur ?? []), ...savedAssociateBookmarkItems]);
		}

		setLoading(false);
	};

	const addBookmarkItem = async itemId => {
		try {
			const newBookmarkItem = new BookmarkItem();
			newBookmarkItem.item = itemId;
			await Promise.allSettled([
				newBookmarkItem.save(),
				CustomerContext.customer ? CustomerContext.addBookmark(itemId) : Promise.resolve(),
			]);
			setBookmarkItems(current => [...(current ?? []), newBookmarkItem]);
			return newBookmarkItem
		}
		catch (e) {
			console.error("Failed to add bookmark", e);
		}
	}

	const removeBookmarkItem = async itemId => {
		const bookmarkItem = bookmarkItems.find(i => i.item?.id === itemId);
		if (bookmarkItem) {
			await Promise.allSettled([
				CustomerContext.customer ? CustomerContext.removeBookmark(bookmarkItem.item.id) : Promise.resolve(),
				bookmarkItem.remove(),
			]);

			setBookmarkItems(current => current.filter(it => it !== bookmarkItem));
		}
	}

	const removeBookmarkItems = async itemIds => {
		const associateBookmarks = bookmarkItems.filter(it => itemIds.includes(it.item.id));
		await Promise.allSettled([
			...associateBookmarks.map(it => it.remove()),
			CustomerContext.customer ? CustomerContext.removeBookmarks(itemIds) : Promise.resolve(),
		]);

		setBookmarkItems(current => current.filter(it => !itemIds.includes(it.item.id)));
	}

	const clearBookmarkItems = async () => {
		// don't use removeBookmarkItem() so bookmarks aren't cleared on sign out
		const results = await Promise.allSettled(bookmarkItems.map(item => item.remove()));
		const failedItem = results.find(result => result.status === "rejected");
		if (failedItem) {
			console.warn("Failed to remove bookmark during clear", failedItem.reason, failedItem.value);
		}
		setBookmarkItems([]);
	}

	React.useEffect(() => {
		!bookmarkItems
			&& getBookmarkItems();
	}, [bookmarkItems, getBookmarkItems]);

	React.useEffect(() => {
		getBookmarkItems();
	}, [currentUser]);

	const isAllowed = catalog => {
		const organization_member_data = JSON.parse(localStorage.getItem('organization_member:data'));
		const organization = organization_member_data?.organization;
		const bookmarksEnabled = organization?.features?.catalog?.lists?.bookmarks !== false;
		if (!bookmarksEnabled) {
			return false;
		}

		return !Catalog.IsPublic(catalog);
	};

	const value = {
		bookmarkItems,
		setBookmarkItems,
		getBookmarkItems,
		addBookmarkItem,
		clearBookmarkItems,
		removeBookmarkItem,
		removeBookmarkItems,
		isAllowed,
		sidebarLayoutMode,
		setSidebarLayoutMode,
	};

	return (
		<BookmarkContext.Provider value={value} displayName="Bookmark Context">
			{children}
		</BookmarkContext.Provider>
	);
};
